// =====================================================
// src/services/cim-generation.service.ts
// Changements :
// =====================================================

import { db } from '@/lib/firebase';
import { collection, addDoc, doc, setDoc, Timestamp, serverTimestamp } from 'firebase/firestore';
import { invokeFunction, invokeFunctionSilent } from '@/lib/cloudrun';
import { SubmitDealFormData } from '@/types/submit-deal.types';
import { performFinancialAnalysis, FinancialAnalysis } from '@/utils/financial-analysis';

export interface CIMData {
  dealId: string;
  content: CIMNarrative;
  financialSummary: FinancialAnalysis;
  status: 'draft_internal' | 'pending_review' | 'approved' | 'sent_to_client';
  createdAt: Date;
  updatedAt: Date;
}

export interface CIMNarrative {
  executiveSummary: string;
  businessOverview: string;
  financialHighlights: string;
  growthOpportunities: string;
  investmentConsiderations: string;
  transitionPlan: string;
  impactProfile: string;
  businessAttractiveness: string;   // E1 — TOV Score qualitative
  valuationAndMultiples: string;    // E2 — Sector multiples
  financingProfile: string;         // E3 — Financing scenarios
}

/**
 * Fonction principale — soumet un deal et génère le CIM
 */
export async function handleSubmitDeal(
  dealData: SubmitDealFormData,
  userId: string,
  userEmail: string
): Promise<{ dealId: string; cimId: string; financialAnalysis: FinancialAnalysis }> {

  // 1. Sauvegarder le deal brut dans Firestore (dealSubmissions)
  const dealRef = await addDoc(collection(db, 'dealSubmissions'), {
    ...dealData,
    userId,
    brokerId: dealData.general?.brokerId || null,
    representedSellerId: dealData.general?.representedSellerId || null,
    representedSellerName: dealData.general?.representedSellerName || null,
    submittedById: userId,
    userEmail,
    status: 'awaiting_review',
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });

  const dealId = dealRef.id;

  // 1b. Sauvegarder dans 'deals' pour le tableau de bord vendeur
  try {
    await setDoc(doc(db, 'deals', dealId), {
      businessName: dealData.general?.tradeName || dealData.general?.legalName || 'Unnamed Business',
      businessDescription: dealData.general?.description || '',
      industry: dealData.general?.industry || '',
      sector: dealData.general?.industry || '',
      askingPrice: dealData.financials?.askingPrice || 0,
      currency: 'USD',
      city: dealData.general?.locationCity || '',
      state: dealData.general?.locationState || '',
      sellerId: dealData.general?.representedSellerId || userId,
      brokerId: dealData.general?.brokerId || null,
      submittedById: userId,
      representedSellerName: dealData.general?.representedSellerName || null,
      representedSellerEmail: dealData.general?.representedSellerEmail || null,
      status: 'under_review',
      viewCount: 0,
      interestedBuyers: [],
      employeeCount: dealData.impact?.employeeCount || 0,
      averageEmployeeTenureYears: dealData.impact?.averageEmployeeTenureYears || 0,
      hasRetirementPlan: dealData.impact?.hasRetirementPlan || false,
      workplaceAccidentsLast3yr: dealData.impact?.workplaceAccidentsLast3yr || 0,
      localSupplyChainPercent: dealData.impact?.localSupplyChainPercent || 0,
      hasEnvironmentalCertification: dealData.impact?.hasEnvironmentalCertification || false,
      hasLitigationHistory: dealData.impact?.hasLitigationHistory || false,
      communityInvolvement: dealData.impact?.communityInvolvement || false,
      impactDataVerified: false,
      impactDataSource: 'self_declared',
      impactVerificationLevel: 'none',
      impactPublishedToLenders: false,
      impactPublishedToBuyers: false,
      ebitdaMargin:
        (dealData.financials?.ebitda ?? 0) > 0 && (dealData.financials?.grossRevenue ?? 0) > 0
          ? (dealData.financials!.ebitda as number) / (dealData.financials!.grossRevenue as number)
          : 0,
      askingMultiple:
        (dealData.financials?.ebitda ?? 0) > 0
          ? (dealData.financials!.askingPrice as number) / (dealData.financials!.ebitda as number)
          : 0,
      naicsCode: dealData.general?.naicsCode || '',
      yearEstablished: dealData.general?.yearEstablished || 0,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
  } catch (firestoreErr) {
    console.error('Failed to save deal to Firestore deals collection:', firestoreErr);
  }

  // Les dashboards staff/admin lisent maintenant depuis Firestore directement.

  // 2. Analyse financière automatique (EBITDA, Multiples)
  const financialAnalysis = performFinancialAnalysis(dealData);

  // 3. Générer la narrative CIM via IA
  let cimNarrative: CIMNarrative;
  try {
    cimNarrative = await generateCIMNarrative(dealData, financialAnalysis);
  } catch (error) {
    console.error('CIM narrative generation failed, using fallback:', error);
    cimNarrative = createFallbackNarrative(dealData, financialAnalysis);
  }

  // 4. Créer l'entrée CIM pour révision staff
  await setDoc(doc(db, 'cimData', dealId), {
    dealId,
    content: cimNarrative,
    financialSummary: financialAnalysis,
    status: 'draft_internal',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // 5. Sauvegarder l'analyse financière séparément
  await setDoc(doc(db, 'financialAnalyses', dealId), {
    dealId,
    ...financialAnalysis,
    createdAt: serverTimestamp(),
  });

  // 6. Notifier le staff pour révision
  await invokeFunctionSilent('send-cim-notification', {
    type: 'cim_ready_for_review',
    dealId,
    dealName: dealData.general?.tradeName || dealData.general?.legalName || 'New Deal',
  });

  // 7. Générer l'étude de marché automatiquement
  try {
    const { data: marketStudyResult } = await invokeFunction('generate-market-study', {
      companyProfile: {
        name: dealData.general?.tradeName || dealData.general?.legalName || 'Company',
        industry: dealData.general?.industry || 'General',
        location: `${dealData.general?.locationCity || ''}, ${dealData.general?.locationCountry || ''}`,
        description: dealData.general?.description || '',
        revenue: dealData.financials?.askingPrice || 0,
        competitors: [],
      },
      studyType: 'full',
      dealId,
    });

    if (marketStudyResult) {
      await setDoc(doc(db, 'marketStudies', dealId), {
        dealId,
        content: marketStudyResult.marketStudy,
        status: 'draft_internal',
        generatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }
  } catch (marketStudyError) {
    console.error('Failed to generate market study (non-blocking):', marketStudyError);
  }

  // 8. Générer le plan stratégique automatiquement
  try {
    const { data: strategicPlanResult } = await invokeFunction('generate-strategic-plan', {
      companyProfile: {
        name: dealData.general?.tradeName || dealData.general?.legalName || 'Company',
        industry: dealData.general?.industry || 'General',
        location: `${dealData.general?.locationCity || ''}, ${dealData.general?.locationCountry || ''}`,
        description: dealData.general?.description || '',
        yearEstablished: dealData.general?.yearEstablished || new Date().getFullYear(),
        employees: dealData.operations?.fullTimeEmployees || 0,
        revenue: dealData.financials?.askingPrice || 0,
        strengths: dealData.swot?.strengths || '',
        weaknesses: dealData.swot?.weaknesses || '',
        opportunities: dealData.swot?.opportunities || '',
        threats: dealData.swot?.threats || '',
      },
      planHorizon: '3-years',
      dealId,
    });

    if (strategicPlanResult) {
      await setDoc(doc(db, 'strategicPlans', dealId), {
        dealId,
        content: strategicPlanResult.strategicPlan,
        status: 'draft_internal',
        generatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }
  } catch (strategicPlanError) {
    console.error('Failed to generate strategic plan (non-blocking):', strategicPlanError);
  }

  return { dealId, cimId: dealId, financialAnalysis };
}

/**
 * Génère la narrative CIM via Claude AI (Cloud Run)
 */
async function generateCIMNarrative(
  dealData: SubmitDealFormData,
  financialAnalysis: FinancialAnalysis
): Promise<CIMNarrative> {
  const { data, error } = await invokeFunction('generate-cim-narrative', {
    dealData: {
      general: dealData.general,
      operations: dealData.operations,
      swot: dealData.swot,
      financials: dealData.financials,
      impact: dealData.impact,
      culture: dealData.culture,  // ← AJOUTER
      financialAnalysis: {
        adjustedEbitda: financialAnalysis.adjustedEbitda,
        ebitdaMultiple: financialAnalysis.ebitdaMultiple,
        revenueMultiple: financialAnalysis.revenueMultiple,
        valuationLow: financialAnalysis.valuationLow,
        valuationMid: financialAnalysis.valuationMid,
        valuationHigh: financialAnalysis.valuationHigh,
        sectorName: financialAnalysis.sectorName,
        sectorMultiple: financialAnalysis.sectorMultiple,
        ebitdaBracket: financialAnalysis.ebitdaBracket,
        grossMargin: financialAnalysis.grossMargin,
        ebitdaMargin: financialAnalysis.ebitdaMargin,
        debtServiceCoverageRatio: financialAnalysis.debtServiceCoverageRatio,
        customerConcentrationRisk: financialAnalysis.customerConcentrationRisk,
        ownerDependencyRisk: financialAnalysis.ownerDependencyRisk,
        revenueGrowthRate: financialAnalysis.revenueGrowthRate,
      },
      tovScore: dealData.tovScore,
    },
  });

  if (error) throw error;
  return data.cimNarrative;
}

/**
 * Narrative de secours si la génération IA échoue
 */
function createFallbackNarrative(
  dealData: SubmitDealFormData,
  financialAnalysis: FinancialAnalysis
): CIMNarrative {
  const { general, operations, swot, financials, impact, tovScore, culture } = dealData;

  // ── E4 — Impact Profile ─────────────────────────────
  const impactProfile = `IMPACT & WORKFORCE PROFILE — Self-declared by seller/broker. Not independently verified by AcquizGroup at this stage.\n\n- Employees: ${impact?.employeeCount ?? 0}\n- Average employee tenure: ${impact?.averageEmployeeTenureYears ?? 0} years\n- Retirement plan offered: ${impact?.hasRetirementPlan ? 'Yes' : 'No'}\n- Workplace accidents (last 3 years): ${impact?.workplaceAccidentsLast3yr ?? 0}\n- Local supply chain: ${impact?.localSupplyChainPercent ?? 0}%\n- Environmental certification: ${impact?.hasEnvironmentalCertification ? 'Yes' : 'No'}\n- Litigation history (last 5 years): ${impact?.hasLitigationHistory ? 'Yes' : 'No'}\n- Community / philanthropic involvement: ${impact?.communityInvolvement ? 'Yes' : 'No'}`;

  // ── E1 — Business Attractiveness (TOV Score qualitative) ──
  const totalScore = tovScore?.totalScore ?? 0;
  let attractivenessRating = 'Below Average';
  let attractivenessDesc = 'This business presents several areas that may require attention from potential buyers prior to acquisition.';
  if (totalScore >= 50) {
    attractivenessRating = 'Excellent';
    attractivenessDesc = 'This business demonstrates strong fundamentals across financial, human capital, market, and operational dimensions.';
  } else if (totalScore >= 38) {
    attractivenessRating = 'Good';
    attractivenessDesc = 'This business presents solid fundamentals with identifiable growth levers and manageable risk factors.';
  } else if (totalScore >= 25) {
    attractivenessRating = 'Average';
    attractivenessDesc = 'This business presents a balanced profile with notable strengths and areas for post-acquisition improvement.';
  }
  const businessAttractiveness = `BUSINESS ATTRACTIVENESS ASSESSMENT — Internal evaluation by AcquizGroup. Not disclosed publicly.\n\nOverall Rating: ${attractivenessRating} (${totalScore}/63)\n\n${attractivenessDesc}\n\nPillar Scores:\n- Financial Health: ${tovScore?.financialScore ?? 0}/17\n- Human Capital: ${tovScore?.humanCapitalScore ?? 0}/17\n- Market & Customers: ${tovScore?.marketScore ?? 0}/16\n- Operations & Governance: ${tovScore?.operationsScore ?? 0}/13\n\nKey Strengths Identified:\n${swot.strengths}\n\nKey Considerations:\n${swot.weaknesses}`;

  // ── E2 — Valuation & Multiples ───────────────────────
  const askingPrice = financials.askingPrice || 0;
  const sectorMid = financialAnalysis.sectorMultiple || 0;
  const priceVsMid = sectorMid > 0 && financialAnalysis.adjustedEbitda > 0
    ? ((askingPrice / financialAnalysis.adjustedEbitda) / sectorMid - 1) * 100
    : 0;
  const pricePositioning = priceVsMid > 15
    ? 'above sector median'
    : priceVsMid < -15
    ? 'below sector median'
    : 'aligned with sector median';
  const valuationAndMultiples = `VALUATION & SECTOR MULTIPLES — Source: Deal Stats Q1 2025\n\nSector: ${financialAnalysis.sectorName}\nEBITDA Bracket: ${financialAnalysis.ebitdaBracket}\n\nSector Multiple (Mid): ${sectorMid.toFixed(1)}x EBITDA\nAsking Multiple: ${financialAnalysis.ebitdaMultiple.toFixed(1)}x EBITDA\n\nValuation Range (AcquizGroup estimate):\n- Conservative: $${financialAnalysis.valuationLow.toLocaleString()}\n- Mid-point: $${financialAnalysis.valuationMid.toLocaleString()}\n- Optimistic: $${financialAnalysis.valuationHigh.toLocaleString()}\n\nAsking price is ${pricePositioning} (${Math.abs(priceVsMid).toFixed(0)}%).\n\nKey Financial Metrics:\n- Gross Margin: ${financialAnalysis.grossMargin.toFixed(1)}%\n- EBITDA Margin: ${financialAnalysis.ebitdaMargin.toFixed(1)}%\n- Revenue Growth (3yr): ${financialAnalysis.revenueGrowthRate.toFixed(1)}%\n- DSCR (est.): ${financialAnalysis.debtServiceCoverageRatio.toFixed(2)}x`;

  // ── E3 — Financing Profile ───────────────────────────
  const loanAmount = askingPrice * 0.8;
  const annuityPayment = loanAmount > 0
    ? (loanAmount * (0.075 / 12) * Math.pow(1 + 0.075 / 12, 120)) /
      (Math.pow(1 + 0.075 / 12, 120) - 1) * 12
    : 0;
  const dscrAnnuity = annuityPayment > 0
    ? financialAnalysis.adjustedEbitda / annuityPayment
    : 0;
  const sbaLoan = askingPrice * 0.9;
  const sbaPayment = sbaLoan > 0
    ? (sbaLoan * (0.085 / 12) * Math.pow(1 + 0.085 / 12, 120)) /
      (Math.pow(1 + 0.085 / 12, 120) - 1) * 12
    : 0;
  const dscrSba = sbaPayment > 0
    ? financialAnalysis.adjustedEbitda / sbaPayment
    : 0;
  const sellerFinancing = dealData.projections?.sellerFinancingOffered
    ? `\n\nSeller Financing Available: Yes\n- Amount: $${(dealData.projections.sellerFinancingAmount || 0).toLocaleString()}\n- Rate: ${dealData.projections.sellerFinancingInterestRate || 0}%\n- Term: ${dealData.projections.sellerFinancingTermMonths || 0} months`
    : '\n\nSeller Financing: Not offered';
  const financingProfile = `FINANCING PROFILE — Educational simulation only. Not a financing offer.\n\nScenario 1 — Conventional / Annuity (80% LTV @ 7.5%, 10 years)\n- Loan Amount: $${loanAmount.toLocaleString()}\n- Annual Debt Service: $${annuityPayment.toLocaleString()}\n- DSCR: ${dscrAnnuity.toFixed(2)}x ${dscrAnnuity >= 1.25 ? '✓ Adequate' : dscrAnnuity >= 1.0 ? '⚠ Marginal' : '✗ Insufficient'}\n\nScenario 2 — SBA Loan (90% LTV @ 8.5%, 10 years)\n- Loan Amount: $${sbaLoan.toLocaleString()}\n- Annual Debt Service: $${sbaPayment.toLocaleString()}\n- DSCR: ${dscrSba.toFixed(2)}x ${dscrSba >= 1.25 ? '✓ Adequate' : dscrSba >= 1.0 ? '⚠ Marginal' : '✗ Insufficient'}${sellerFinancing}\n\nNote: Actual financing terms depend on lender underwriting, buyer creditworthiness, and current market conditions.`;

  return {
    executiveSummary: `${general.tradeName || general.legalName} is a ${general.industry} business located in ${general.locationCity}, ${general.locationCountry}. Established in ${general.yearEstablished}, the company has demonstrated consistent performance with an adjusted EBITDA of $${financialAnalysis.adjustedEbitda.toLocaleString()}.`,
    businessOverview: `${general.description}\n\nThe business employs ${operations.fullTimeEmployees} full-time and ${operations.partTimeEmployees} part-time employees. Current owner involvement is ${operations.ownerHoursPerWeek} hours per week.`,
    financialHighlights: `- Asking Price: $${financials.askingPrice.toLocaleString()}\n- Adjusted EBITDA: $${financialAnalysis.adjustedEbitda.toLocaleString()}\n- EBITDA Multiple: ${financialAnalysis.ebitdaMultiple.toFixed(1)}x\n- Sector Multiple (${financialAnalysis.sectorName}): ${financialAnalysis.sectorMultiple.toFixed(1)}x\n- Cash Flow: $${financials.cashFlowEquity.toLocaleString()}`,
    growthOpportunities: swot.opportunities,
    investmentConsiderations: `Strengths:\n${swot.strengths}\n\nKey Considerations:\n${swot.weaknesses}`,
    transitionPlan: (() => {
  const c = culture;
  const lines: string[] = [];

  // Core values
  if (c?.coreValues?.length > 0) {
    const vals = c.coreValues
      .map((v: string, i: number) => {
        const desc = c.coreValueDescriptions?.[i];
        return v ? `• ${v}${desc ? `: ${desc}` : ''}` : null;
      })
      .filter(Boolean);
    if (vals.length > 0) {
      lines.push(`Core Company Values:\n${vals.join('\n')}`);
    }
  }

  // Management team
  if (c?.managementTeam === 'yes' && c?.managementTeamExplanation) {
    lines.push(`Management Team: ${c.managementTeamExplanation}`);
  } else if (c?.managementTeam === 'no') {
    lines.push(`Management Team: Owner-operated business.`);
  }

  // Post-sale consulting
  const consultingMap: Record<string, string> = {
    yes_full_time: 'Full-time consulting available',
    yes_part_time: 'Part-time consulting available',
    yes_on_demand: 'On-demand consulting available',
    negotiable: 'Consulting availability negotiable',
    no: 'No post-sale consulting available',
  };
  const consulting = consultingMap[c?.postSaleConsultingAvailability] || 'Transition support available';
  lines.push(`Post-Sale Consulting: ${consulting}${c?.postSaleConsultingDetails ? ` — ${c.postSaleConsultingDetails}` : ''}`);

  // Transition duration
  const durationMap: Record<string, string> = {
    '30_days': '30 days', '60_days': '60 days', '90_days': '90 days',
    '6_months': '6 months', '12_months': '12 months', 'more_than_90_days': 'More than 90 days',
  };
  if (c?.transitionDurationPreference) {
    lines.push(`Transition Duration: ${durationMap[c.transitionDurationPreference] || c.transitionDurationPreference}`);
  }

  // Non-compete
  if (c?.nonCompetePeriod) {
    lines.push(`Non-Compete Period: ${c.nonCompetePeriod.replace('_', ' ')}`);
  }

  // Ideal successor
  if (c?.idealSuccessorDescription) {
    lines.push(`Ideal Successor Profile: ${c.idealSuccessorDescription}`);
  }

  // Deal breakers
  if (c?.dealBreakersFromSeller) {
    lines.push(`Seller's Non-Negotiable Conditions: ${c.dealBreakersFromSeller}`);
  }

  // Owner dependency fallback
  lines.push(`Owner Dependency Risk: ${financialAnalysis.ownerDependencyRisk}`);

  return lines.join('\n\n');
})(),
    impactProfile,
    businessAttractiveness,
    valuationAndMultiples,
    financingProfile,
  };
}

/**
 * Met à jour le statut du CIM
 */
export async function updateCIMStatus(cimId: string, status: CIMData['status']): Promise<void> {
  await setDoc(doc(db, 'cimData', cimId), { status, updatedAt: serverTimestamp() }, { merge: true });
}

/**
 * Met à jour le contenu du CIM après révision staff
 */
export async function updateCIMContent(cimId: string, content: Partial<CIMNarrative>): Promise<void> {
  await setDoc(doc(db, 'cimData', cimId), { content, updatedAt: serverTimestamp() }, { merge: true });
}